import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowRight,
  BrainCircuit,
  Check,
  ChevronRight,
  CircleDot,
  Menu,
  MousePointer2,
  Pause,
  Play,
  Route,
  ShieldCheck,
  X,
} from "lucide-react";
import { gsap } from "gsap";
import { BRAND } from "./brand";
import { DataEvidenceSection } from "./components/DataEvidenceSection";
import { FAQSection } from "./components/FAQSection";
import { SpiderField } from "./components/SpiderField";
import { ChapterScrubber, type Chapter } from "./components/ui/chapter-scrubber";
import { CursorFollowingGlassButton } from "./components/ui/CursorFollowingGlassButton";
import { MagneticCursor } from "./components/ui/MagneticCursor";
import { WaitlistExperience, type AccessMode } from "./components/ui/light-theme-waitlist-landing-page-with-countdown";
import NavigationDustMorph from "./components/ui/navigation-dust-morph";
import { TestimonialsColumn, type Testimonial } from "./components/ui/testimonials-columns-1";
import { TextHoverEffect } from "./components/ui/TextHoverEffect";
import TubesCursor from "./components/ui/tubes-curor";

const demoVideoUrl = new URL("../assets/demo-beta.mp4", import.meta.url).href;

const chapters = [
  { id: "evidence", label: "Evidence" },
  { id: "voices", label: "Voices" },
  { id: "agent", label: "Agent" },
  { id: "data", label: "Data" },
  { id: "report", label: "Report" },
  { id: "access", label: "Access" },
  { id: "faq", label: "FAQ" },
  { id: "footer", label: "Close" },
] as const;

const chapterPreviews: Chapter[] = [
  {
    id: "evidence",
    meta: "02 / Evidence",
    title: "One workspace. Every proof.",
    description: "Source, revisions, builds, runs, and artifacts stay visibly connected.",
  },
  {
    id: "voices",
    meta: "03 / Voices",
    title: "Placeholder voices.",
    description: "Composition previews awaiting verified customer testimony.",
  },
  {
    id: "agent",
    meta: "04 / Agent",
    title: "Deep context. Precisely routed intelligence.",
    description: "Knowledge injection grounds each task; capability-aware routing applies the model it needs.",
  },
  {
    id: "data",
    meta: "05 / Data",
    title: "Order flow, without the fog.",
    description: "A 29,485-event synthetic replay resolving into sixty inspectable one-minute bars.",
  },
  {
    id: "report",
    meta: "06 / Report",
    title: "A report you can interrogate.",
    description: "Performance, costs, warnings, and source identity share one result plane.",
  },
  {
    id: "access",
    meta: "07 / Access",
    title: "Get a place at the workbench.",
    description: "Join the public list, request beta access, or use an invite to create your account.",
  },
  {
    id: "faq",
    meta: "08 / FAQ",
    title: "Questions worth asking.",
    description: "Straight answers about the beta, its boundaries, and the evidence behind each result.",
  },
  {
    id: "footer",
    meta: "09 / Close",
    title: "Rigorous research for retail traders.",
    description: "The working name is M4MMON. The beta remains invite-only.",
  },
];

const sectionIds = ["top", ...chapters.map(({ id }) => id)] as const;
const SECTION_NAVIGATION_EVENT = "m4mmon:navigate";

function requestSectionNavigation(id: (typeof sectionIds)[number], history: "push" | "replace" = "push") {
  window.dispatchEvent(new CustomEvent(SECTION_NAVIGATION_EVENT, { detail: { history, id } }));
}

function scrollToAccess(mode: AccessMode, setMode: (mode: AccessMode) => void) {
  setMode(mode);
  requestSectionNavigation("access");
}

function getInitialNavigationCondensed() {
  const initialSection = window.location.hash.slice(1);
  if (initialSection) return initialSection !== "top";
  return window.scrollY > Math.max(48, window.innerHeight * 0.08);
}

function LogoMark({ className }: { className: string }) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 32 32">
      <path d="M6 11.5 16 5l10 6.5-10 6.3L6 11.5Z" />
      <path d="m6 17.4 10 6.4 10-6.4" />
    </svg>
  );
}

function Logo({ magnetic = false }: { magnetic?: boolean }) {
  return (
    <a
      aria-label={`${BRAND.name} home`}
      className="logo"
      data-footer-magnetic={magnetic ? "" : undefined}
      href="#top"
    >
      <LogoMark className="logo__mark" />
      <span>{BRAND.name}</span>
    </a>
  );
}

function Header({ condensed, setMode }: { condensed: boolean; setMode: (mode: AccessMode) => void }) {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const openMenu = () => {
    setClosing(false);
    setOpen(true);
  };

  const closeMenu = useCallback((restoreFocus = false) => {
    if (!open) return;
    if (restoreFocus) menuButtonRef.current?.focus();
    setOpen(false);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setClosing(false);
      return;
    }
    setClosing(true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      closeMenu(true);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [closeMenu, open]);

  return (
    <header className={`site-header${condensed ? " is-condensed" : ""}`}>
      <nav aria-label="Primary" className="nav-shell">
        <Logo />
        <div
          aria-hidden={!open || undefined}
          className={`nav-shell__links${open ? " is-open" : ""}${closing ? " is-closing" : ""}`}
          id="mobile-navigation"
          onAnimationEnd={(event) => { if (event.target === event.currentTarget && event.animationName === "mobile-navigation-dismiss") setClosing(false); }}
        >
          <a href="#agent" onClick={() => closeMenu(true)}>Agent system</a>
          <a href="#data" onClick={() => closeMenu(true)}>Data</a>
          <a className="nav-shell__login" href="/login" onClick={() => closeMenu(true)}>Log in</a>
          <button
            className="nav-shell__access primary-glass-button"
            onClick={() => {
              scrollToAccess("public_waitlist", setMode);
              closeMenu(true);
            }}
          >
            Join waitlist <ArrowRight aria-hidden="true" size={15} />
          </button>
        </div>
        <button
          aria-expanded={open}
          aria-controls="mobile-navigation"
          aria-label={open ? "Close menu" : "Open menu"}
          className="nav-shell__menu"
          onClick={() => { if (open) closeMenu(); else openMenu(); }}
          ref={menuButtonRef}
        >
          {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
      </nav>
    </header>
  );
}

function ChapterRail({ activeIndex, visible }: { activeIndex: number; visible: boolean }) {
  const footerMagnetic = activeIndex === chapterPreviews.length - 1;

  return (
    <aside
      aria-hidden={!visible}
      aria-label="Page chapters"
      className={`chapter-rail${visible ? " is-visible" : ""}`}
      inert={!visible}
    >
      <div className="chapter-rail__assembly">
        <a
          aria-label="Back to top"
          className="chapter-rail__brand"
          data-footer-magnetic={footerMagnetic ? "" : undefined}
          href="#top"
        >
          <span aria-hidden="true" className="cursor-following-glass-button__material" />
          <span aria-hidden="true" className="cursor-following-glass-button__rim" />
          <LogoMark className="chapter-rail__mark" />
        </a>
        <ChapterScrubber
          chapters={chapterPreviews}
          currentIndex={visible ? Math.max(0, activeIndex) : undefined}
          label="Page chapters"
          magnetic={footerMagnetic}
          onSelect={(_, index) => {
            const destination = chapters[index]?.id;
            if (destination) requestSectionNavigation(destination);
          }}
        />
      </div>
    </aside>
  );
}

function ScrollMorph({ activeIndex }: { activeIndex: number }) {
  const hidden = activeIndex >= 0;
  return (
    <a
      aria-hidden={hidden}
      aria-label="Enter the workspace"
      className={`scroll-morph${hidden ? " is-hidden" : ""}`}
      href="#evidence"
      inert={hidden}
      tabIndex={hidden ? -1 : undefined}
    >
      <span className="scroll-morph__hero-label">Enter the workspace</span>
      <ArrowDown aria-hidden="true" size={14} />
    </a>
  );
}

function MobileAccessDock({ activeIndex, setMode }: { activeIndex: number; setMode: (mode: AccessMode) => void }) {
  if (activeIndex < 0 || activeIndex >= chapterPreviews.length - 1) return null;
  return (
    <button className="mobile-access-dock primary-glass-button" onClick={() => scrollToAccess("public_waitlist", setMode)}>
      Join waitlist <ArrowRight aria-hidden="true" size={15} />
    </button>
  );
}

function MobileProgress({ progress }: { progress: number }) {
  return (
    <div aria-hidden="true" className="mobile-progress">
      <span style={{ transform: `scaleX(${progress})` }} />
    </div>
  );
}

function Hero({ cursorActive, setMode }: { cursorActive: boolean; setMode: (mode: AccessMode) => void }) {
  return (
    <section className="hero chapter" id="top">
      <TubesCursor active={cursorActive} />
      <div className="hero__content">
        <h1>Turn trading ideas<br />into evidence.</h1>
        <p>
          Build real Python strategies, test them on covered order-flow data, and inspect every result—inside one rigorous workspace.
        </p>
        <div className="hero__actions" aria-label="Access options">
          <button className="hero-cta hero-cta--primary primary-glass-button" onClick={() => scrollToAccess("public_waitlist", setMode)}>
            <span>Join the public waitlist</span>
            <ArrowRight aria-hidden="true" size={18} />
          </button>
          <button className="hero-cta hero-cta--secondary" onClick={() => scrollToAccess("invite_beta", setMode)}>
            Request beta access
          </button>
          <button className="hero-cta hero-cta--tertiary" onClick={() => scrollToAccess("redeem", setMode)}>
            Create account with invite <ChevronRight aria-hidden="true" size={15} />
          </button>
        </div>
      </div>
      <div className="hero__status" aria-label="Beta status">
        <span className="status-light" /> Invite-only beta · No live trading
      </div>
    </section>
  );
}

function EvidenceSection({ setMode }: { setMode: (mode: AccessMode) => void }) {
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastPlaybackTimeRef = useRef(0);
  const userPausedRef = useRef(false);
  const [videoSource, setVideoSource] = useState("");
  const [videoState, setVideoState] = useState<"loading" | "playing" | "paused" | "error">("loading");
  const [hasCompletedPreview, setHasCompletedPreview] = useState(false);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (!videoSource) {
      userPausedRef.current = false;
      setVideoSource(demoVideoUrl);
      requestAnimationFrame(() => void videoRef.current?.play().catch(() => {
        userPausedRef.current = true;
        setVideoState("paused");
      }));
      return;
    }
    if (video.paused) {
      userPausedRef.current = false;
      void video.play().catch(() => {
        userPausedRef.current = true;
        setVideoState("paused");
      });
    } else {
      userPausedRef.current = true;
      video.pause();
    }
  };

  const handleVideoTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;

    if (lastPlaybackTimeRef.current > video.currentTime + 0.5) {
      setHasCompletedPreview(true);
    }
    lastPlaybackTimeRef.current = video.currentTime;
  };

  useEffect(() => {
    const section = sectionRef.current;
    const video = videoRef.current;
    if (!section || !video) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const saveData = Boolean((navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData);
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting && entry.intersectionRatio > 0.12 && !videoSource) {
        if (saveData) setVideoState("paused");
        else setVideoSource(demoVideoUrl);
        return;
      }
      if (entry?.isIntersecting && entry.intersectionRatio > 0.35 && videoSource && !reducedMotion && !saveData && !userPausedRef.current) {
        void video.play().catch(() => {
          userPausedRef.current = true;
          setVideoState("paused");
        });
      } else if (!entry?.isIntersecting || entry.intersectionRatio < 0.12) {
        video.pause();
      }
    }, { threshold: [0, 0.12, 0.35, 0.65] });
    observer.observe(section);
    return () => observer.disconnect();
  }, [videoSource]);

  return (
    <section className="chapter evidence" id="evidence" ref={sectionRef}>
      <div className="section-copy section-copy--floating">
        <h2>One workspace.<br />Every proof.</h2>
        <p>Source, revisions, builds, runs, and artifacts stay visibly connected—so a result never loses its provenance.</p>
      </div>
      <div className="evidence__stage">
        <video
          loop
          muted
          playsInline
          preload="metadata"
          onCanPlay={() => setVideoState((current) => current === "loading" ? "paused" : current)}
          onEnded={() => setHasCompletedPreview(true)}
          onError={() => setVideoState("error")}
          onLoadedMetadata={() => {
            lastPlaybackTimeRef.current = 0;
          }}
          onPause={() => setVideoState("paused")}
          onPlay={() => setVideoState("playing")}
          onTimeUpdate={handleVideoTimeUpdate}
          ref={videoRef}
          src={videoSource || undefined}
        />
        <div className="evidence__scrim" />
        {videoState === "loading" && <div className="video-status">Loading product motion study…</div>}
        {videoState === "error" && <div className="video-status">The product preview could not be loaded.</div>}
        <span className="evidence__disclosure">Product motion study · replace before launch</span>
      </div>
      <button
        aria-label={videoState === "playing" ? "Pause product preview" : "Play product preview"}
        className="video-control"
        onClick={togglePlayback}
        type="button"
      >
        {videoState === "playing" ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
      </button>
      <CursorFollowingGlassButton
        containerRef={sectionRef}
        onClick={() => scrollToAccess("public_waitlist", setMode)}
        visible={hasCompletedPreview || videoState === "error"}
      >
        <span>Join the waitlist</span>
        <MousePointer2 aria-hidden="true" size={16} />
      </CursorFollowingGlassButton>
    </section>
  );
}

const testimonials = [
  {
    quote: "I stopped arguing with a chart and started interrogating the strategy. The difference was immediate.",
    role: "Discretionary systems",
  },
  {
    quote: "The revision-to-result chain is the first time I have felt confident I could reproduce what I was looking at.",
    role: "Python-assisted research",
  },
  {
    quote: "It feels serious without assuming I already run a quant desk. That balance is rare.",
    role: "Order-flow research",
  },
  {
    quote: "Seeing the build, dataset, and source revision together made the result easier to question—not easier to trust blindly.",
    role: "Systematic strategy research",
  },
  {
    quote: "The agent did enough to help without disappearing into an open-ended workflow I could not inspect.",
    role: "Independent Python trader",
  },
  {
    quote: "The constraints are visible. I can tell which market, date range, and revision produced the output.",
    role: "Evidence-led research",
  },
  {
    quote: "I could follow the path from a strategy idea to a completed run without assembling a separate toolchain.",
    role: "Retail quant research",
  },
  {
    quote: "Failures are useful because they say whether the problem came from the project, build, data coverage, or run.",
    role: "Backtest development",
  },
  {
    quote: "It gives Python-assisted research structure without flattening the details that matter to an experienced trader.",
    role: "Multi-strategy research",
  },
] as const;

const testimonialColumns: Testimonial[][] = [0, 1, 2].map((columnIndex) =>
  testimonials.slice(columnIndex * 3, columnIndex * 3 + 3).map((testimonial, rowIndex) => ({
    text: testimonial.quote,
    name: `Placeholder profile ${String(columnIndex * 3 + rowIndex + 1).padStart(2, "0")}`,
    role: testimonial.role,
  })),
);

function TestimonialsSection() {
  return (
    <section className="chapter testimonials" id="voices">
      <div className="chapter-frame testimonials__layout">
        <header className="testimonials__intro">
          <div>
            <h2>What our users say</h2>
            <p>A motion and layout preview using placeholder voices. Replace every quote and profile with verified customer testimony before launch.</p>
            <span>Placeholder testimony · not production evidence</span>
          </div>
        </header>
        <div className="testimonials__columns">
          <TestimonialsColumn duration={17} testimonials={testimonialColumns[0] ?? []} />
          <TestimonialsColumn className="testimonials-column--tablet" duration={21} testimonials={testimonialColumns[1] ?? []} />
          <TestimonialsColumn className="testimonials-column--desktop" duration={19} testimonials={testimonialColumns[2] ?? []} />
        </div>
      </div>
    </section>
  );
}

function AgentSection() {
  const contextSignals = ["Strategy source", "Exact revision", "Research contract", "Data + run state"];
  const modelLanes = [
    ["Routine retrieval", "Efficient"],
    ["Code + analysis", "Balanced"],
    ["Hard reasoning", "Near-frontier"],
  ];
  const agentActions = [
    ["LIST", "project files"],
    ["READ", "source + state"],
    ["EDIT", "conflict-safe"],
    ["CHECK", "requirements"],
    ["BUILD", "exact revision"],
    ["RUN", "one backtest"],
  ];

  return (
    <section aria-labelledby="agent-title" className="chapter agent" id="agent">
      <SpiderField />
      <div className="chapter-frame agent__layout">
        <header className="agent__copy-plane agent__story">
          <h2 id="agent-title">Deep context. Precisely routed intelligence.</h2>
          <p>
            M4MMON’s coding agent begins with focused project knowledge, then routes each step to the
            most cost-efficient model capable of doing it well.
          </p>
          <p className="agent__outcome">
            Designed for near-frontier capability on demanding work—without sending routine tasks
            down the most expensive path.
          </p>
        </header>

        <figure aria-labelledby="agent-system-title" className="agent__plane">
          <figcaption className="agent__prompt">
            <CircleDot aria-hidden="true" size={15} />
            <div>
              <span>Example task</span>
              <blockquote>“Fix the warm-up error, build this exact revision, then run one backtest.”</blockquote>
            </div>
          </figcaption>

          <ol aria-label="How the agent handles one task" className="agent__system-flow">
            <li className="agent__stage agent__stage--knowledge">
              <div className="agent__stage-label">
                <BrainCircuit aria-hidden="true" size={16} />
                <span>01 / Knowledge injection</span>
              </div>
              <h3 id="agent-system-title">Grounded before it acts.</h3>
              <p>A proprietary context layer assembles only the strategy and research knowledge the task needs.</p>
              <ul aria-label="Injected project knowledge" className="agent__context-signals">
                {contextSignals.map((signal) => <li key={signal}>{signal}</li>)}
              </ul>
            </li>

            <li className="agent__stage agent__stage--routing">
              <div className="agent__stage-label">
                <Route aria-hidden="true" size={16} />
                <span>02 / Model routing</span>
              </div>
              <h3>Capability matched to the work.</h3>
              <p>Routine steps stay lean. Harder reasoning escalates only when it earns the cost.</p>
              <ul aria-label="Model routing paths" className="agent__model-lanes">
                {modelLanes.map(([task, path], index) => (
                  <li className={index === modelLanes.length - 1 ? "is-escalated" : ""} key={task}>
                    <span>{task}</span>
                    <strong>{path}</strong>
                  </li>
                ))}
              </ul>
            </li>

            <li className="agent__stage agent__stage--execution">
              <div className="agent__execution-copy">
                <div className="agent__stage-label">
                  <ShieldCheck aria-hidden="true" size={16} />
                  <span>03 / Bounded action</span>
                </div>
                <h3>Every move stays inspectable.</h3>
              </div>
              <ul aria-label="Available agent actions" className="agent__actions">
                {agentActions.map(([verb, object]) => (
                  <li key={verb}>
                    <Check aria-hidden="true" size={12} />
                    <span>{verb}</span>
                    <small>{object}</small>
                  </li>
                ))}
              </ul>
            </li>
          </ol>

          <aside aria-label="Agent safeguards" className="agent__safeguards">
            <ShieldCheck aria-hidden="true" size={14} />
            <p>No arbitrary runtime network · No runtime credentials · No silent source changes</p>
          </aside>
        </figure>
      </div>
    </section>
  );
}

const summaryMetrics = [
  ["Total return", "+18.42%"],
  ["Maximum drawdown", "−7.18%"],
  ["Win rate", "54.90%"],
  ["Profit factor", "1.62"],
  ["Trade count", "102"],
  ["Final equity", "$59,210"],
];

function ReportSection() {
  return (
    <section className="chapter report" id="report">
      <div className="chapter-frame report__layout">
        <div className="section-copy">
          <h2>A report you<br />can interrogate.</h2>
          <p>Performance, costs, warnings, and source identity land in one result plane—not a confetti screen.</p>
        </div>
        <div className="report-plane">
          <div className="report-plane__head">
            <div><span>RESULT / SYNTHETIC</span><strong>BTC order-flow reversal</strong></div>
            <span className="report-plane__success"><Check aria-hidden="true" size={13} /> Succeeded</span>
          </div>
          <div className="report-plane__body">
            <div className="report-plane__curve">
              <div className="report-plane__curve-meta"><span>Equity</span><strong>$59,210</strong></div>
              <svg aria-label="Synthetic backtest equity curve" role="img" viewBox="0 0 620 220">
                <defs>
                  <linearGradient id="equityFill" x1="0" x2="0" y1="0" y2="1">
                    <stop stopColor="#785cff" stopOpacity=".26" /><stop offset="1" stopColor="#785cff" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path className="report-plane__area" d="M0 194 C45 188 63 151 99 162 S159 180 197 132 258 112 292 121 350 75 394 92 448 45 482 58 550 27 620 20 L620 220 L0 220Z" />
                <path className="report-plane__line" d="M0 194 C45 188 63 151 99 162 S159 180 197 132 258 112 292 121 350 75 394 92 448 45 482 58 550 27 620 20" />
              </svg>
              <div className="report-plane__dates"><span>02 JAN</span><span>31 MAR</span><span>28 JUN</span></div>
            </div>
            <dl className="report-plane__metrics">
              {summaryMetrics.map(([term, value]) => <div key={term}><dt>{term}</dt><dd>{value}</dd></div>)}
            </dl>
          </div>
          <div className="report-plane__foot">
            <span>revision 8d271e</span><span>build bld_0318</span><span>dataset binance-usdt-spot-orderflow-1m-v1</span><span>0 fatal errors</span>
          </div>
        </div>
        <p className="report__disclosure">All values in this report are synthetic and provided only to demonstrate the result schema.</p>
      </div>
    </section>
  );
}

function Footer({ active, setMode }: { active: boolean; setMode: (mode: AccessMode) => void }) {
  const footerRef = useRef<HTMLElement>(null);

  return (
    <footer className="chapter footer" id="footer" ref={footerRef}>
      <MagneticCursor active={active} externalScopeSelector=".chapter-rail" scopeRef={footerRef} />
      <div className="footer__top">
        <Logo magnetic />
        <p>Rigorous strategy research for retail traders who want more than a persuasive chart.</p>
        <div className="footer__actions">
          <a className="footer__login-glass" data-footer-magnetic href="/login">
            <span aria-hidden="true" className="cursor-following-glass-button__material" />
            <span aria-hidden="true" className="cursor-following-glass-button__rim" />
            <span className="footer__login-label">Log in</span>
          </a>
          <button data-footer-magnetic onClick={() => scrollToAccess("public_waitlist", setMode)}>Join the waitlist <ArrowRight aria-hidden="true" size={16} /></button>
        </div>
      </div>
      <div className="footer__links">
        <div><span>Explore</span><a data-footer-magnetic href="#agent">Agent system</a><a data-footer-magnetic href="#report">Result schema</a><a data-footer-magnetic href="#faq">FAQ</a></div>
        <div><span>Access</span><a data-footer-magnetic href="/login">Log in</a><button data-footer-magnetic onClick={() => scrollToAccess("public_waitlist", setMode)}>Public waitlist</button><button data-footer-magnetic onClick={() => scrollToAccess("invite_beta", setMode)}>Beta request</button><button data-footer-magnetic onClick={() => scrollToAccess("redeem", setMode)}>Use invite</button></div>
        <div><span>Boundaries</span><a data-footer-magnetic href="#data">Covered data</a><a data-footer-magnetic href="#top">No live trading</a><a data-footer-magnetic href="#voices">Social features: future</a></div>
        <div className="footer__legal">
          <span>Legal</span>
          <a data-footer-magnetic href="/access-request-email.html">Access-request email</a>
          <a data-footer-magnetic href="/privacy.html">Privacy policy</a>
          <a data-footer-magnetic href="/terms.html">Terms of service</a>
        </div>
      </div>
      <div className="footer__wordmark" aria-label={BRAND.name} role="img">
        <TextHoverEffect text={BRAND.name} />
      </div>
      <div className="footer__bottom">
        <span>© 2026 {BRAND.name}. Working name.</span>
        <span>Invite-only beta · Research software, not investment advice.</span>
      </div>
    </footer>
  );
}

export default function App() {
  const [accessMode, setAccessMode] = useState<AccessMode>("public_waitlist");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [navigationCondensed, setNavigationCondensed] = useState(getInitialNavigationCondensed);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const sections = sectionIds.map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    const desktopPager = window.matchMedia("(min-width: 1024px) and (pointer: fine) and (prefers-reduced-motion: no-preference)");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let activeSectionIndex = 0;
    let animationFrame = 0;
    let scrollFrame = 0;
    let hashTimer = 0;
    let wheelAccumulator = 0;
    let wheelIgnoreUntil = 0;
    let isAnimating = false;
    let navigationIsCondensed = getInitialNavigationCondensed();
    let navigationMeasured = false;
    let previousNavigationScrollY = window.scrollY;
    let upwardTravel = 0;

    const getViewportHeight = () => Math.round(window.visualViewport?.height ?? window.innerHeight);
    const isPagedMode = () => desktopPager.matches && getViewportHeight() >= 720;

    const updateViewportHeight = () => {
      document.documentElement.style.setProperty("--visual-viewport-height", `${getViewportHeight()}px`);
    };

    const writeHash = (id: string, mode: "push" | "replace" | "none") => {
      if (mode === "none") return;
      const hash = `#${id}`;
      if (window.location.hash === hash) return;
      const method = mode === "push" ? "pushState" : "replaceState";
      window.history[method](null, "", hash);
    };

    const updateActiveSection = (syncHash = true) => {
      const viewportHeight = getViewportHeight();
      const focusLine = viewportHeight * 0.5;
      const currentScrollY = window.scrollY;
      const navigationDelta = currentScrollY - previousNavigationScrollY;
      let candidate = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= focusLine && rect.bottom > focusLine) {
          candidate = index;
          nearestDistance = 0;
          return;
        }
        if (nearestDistance === 0) return;
        const distance = Math.min(Math.abs(rect.top - focusLine), Math.abs(rect.bottom - focusLine));
        if (distance < nearestDistance) {
          candidate = index;
          nearestDistance = distance;
        }
      });

      activeSectionIndex = candidate;
      setActiveIndex(candidate - 1);
      if (navigationDelta < -0.5) {
        upwardTravel += -navigationDelta;
      } else if (navigationDelta > 0.5) {
        upwardTravel = 0;
      }

      const forwardThreshold = Math.max(48, viewportHeight * 0.08);
      const reverseThreshold = viewportHeight * 0.72;
      let nextNavigationState = navigationIsCondensed;

      if (!navigationMeasured) {
        nextNavigationState = navigationIsCondensed || currentScrollY > forwardThreshold;
        navigationMeasured = true;
      } else if (!navigationIsCondensed && navigationDelta > 0.5 && currentScrollY > forwardThreshold) {
        nextNavigationState = true;
      } else if (
        navigationIsCondensed &&
        navigationDelta < -0.5 &&
        upwardTravel > 28 &&
        currentScrollY < reverseThreshold
      ) {
        nextNavigationState = false;
      }

      if (nextNavigationState !== navigationIsCondensed) {
        navigationIsCondensed = nextNavigationState;
        setNavigationCondensed(nextNavigationState);
      }
      previousNavigationScrollY = currentScrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, currentScrollY / max) : 0);

      if (syncHash && !isAnimating) {
        window.clearTimeout(hashTimer);
        hashTimer = window.setTimeout(() => writeHash(sectionIds[candidate] ?? "top", "replace"), 120);
      }
    };

    const onScroll = () => {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = 0;
        updateActiveSection();
      });
    };

    const scrollToSection = (index: number, historyMode: "push" | "replace" | "none") => {
      const destinationIndex = Math.max(0, Math.min(index, sections.length - 1));
      const destination = sections[destinationIndex];
      if (!destination) return;
      const destinationY = destination.getBoundingClientRect().top + window.scrollY;

      window.cancelAnimationFrame(animationFrame);

      if (!isPagedMode()) {
        writeHash(sectionIds[destinationIndex] ?? "top", historyMode);
        destination.scrollIntoView({ behavior: reducedMotion.matches ? "auto" : "smooth", block: "start" });
        return;
      }

      isAnimating = true;
      document.documentElement.dataset.chapterSettling = "true";
      let position = window.scrollY;
      let velocity = 0;
      let previousTime = performance.now();
      const startedAt = previousTime;

      const finish = () => {
        window.scrollTo(0, destinationY);
        isAnimating = false;
        wheelAccumulator = 0;
        wheelIgnoreUntil = performance.now() + 260;
        delete document.documentElement.dataset.chapterSettling;
        activeSectionIndex = destinationIndex;
        setActiveIndex(destinationIndex - 1);
        writeHash(sectionIds[destinationIndex] ?? "top", historyMode);
        updateActiveSection(false);
      };

      const settle = (now: number) => {
        const deltaTime = Math.min((now - previousTime) / 1000, 0.032);
        previousTime = now;
        const acceleration = (destinationY - position) * 55 - velocity * 14.8;
        velocity += acceleration * deltaTime;
        position += velocity * deltaTime;
        window.scrollTo(0, position);

        if ((Math.abs(destinationY - position) < 0.35 && Math.abs(velocity) < 3) || now - startedAt > 1600) {
          finish();
          return;
        }
        animationFrame = window.requestAnimationFrame(settle);
      };

      animationFrame = window.requestAnimationFrame(settle);
    };

    const canNestedScrollerMove = (target: EventTarget | null, deltaY: number) => {
      let element = target instanceof Element ? target : null;
      while (element && element !== document.body) {
        const style = window.getComputedStyle(element);
        const scrollable = /(auto|scroll)/.test(style.overflowY) && element.scrollHeight > element.clientHeight + 1;
        if (scrollable) {
          const canMoveDown = deltaY > 0 && element.scrollTop + element.clientHeight < element.scrollHeight - 1;
          const canMoveUp = deltaY < 0 && element.scrollTop > 1;
          if (canMoveDown || canMoveUp) return true;
        }
        element = element.parentElement;
      }
      return false;
    };

    const onWheel = (event: WheelEvent) => {
      const focusedElement = document.activeElement instanceof Element ? document.activeElement : null;
      const editing = Boolean(focusedElement?.matches("input, textarea, select, [contenteditable='true']"));
      const menuOpen = Boolean(document.querySelector(".nav-shell__links.is-open"));
      const selectingText = window.getSelection()?.type === "Range";
      if (!isPagedMode() || event.ctrlKey || editing || menuOpen || selectingText || canNestedScrollerMove(event.target, event.deltaY)) return;

      event.preventDefault();
      if (isAnimating || performance.now() < wheelIgnoreUntil) return;

      const multiplier = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? 16 : event.deltaMode === WheelEvent.DOM_DELTA_PAGE ? getViewportHeight() : 1;
      wheelAccumulator += event.deltaY * multiplier;
      if (Math.abs(wheelAccumulator) < 44) return;

      const direction = wheelAccumulator > 0 ? 1 : -1;
      wheelAccumulator = 0;
      scrollToSection(activeSectionIndex + direction, "replace");
    };

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (!isPagedMode() || event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest("input, textarea, select, button, a, [contenteditable='true']")) return;

      const nextKeys = ["ArrowDown", "PageDown", " "];
      const previousKeys = ["ArrowUp", "PageUp"];
      let destination: number | null = null;
      if (nextKeys.includes(event.key)) destination = activeSectionIndex + 1;
      if (previousKeys.includes(event.key)) destination = activeSectionIndex - 1;
      if (event.key === "Home") destination = 0;
      if (event.key === "End") destination = sections.length - 1;
      if (destination === null) return;

      event.preventDefault();
      scrollToSection(destination, "replace");
    };

    const onAnchorClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest<HTMLAnchorElement>('a[href^="#"]');
      const id = anchor?.hash.slice(1);
      const destinationIndex = id ? sectionIds.indexOf(id as (typeof sectionIds)[number]) : -1;
      if (!anchor || destinationIndex < 0) return;
      event.preventDefault();
      scrollToSection(destinationIndex, "push");
    };

    const onNavigationRequest = (event: Event) => {
      const { history, id } = (event as CustomEvent<{ history: "push" | "replace"; id: (typeof sectionIds)[number] }>).detail;
      const destinationIndex = sectionIds.indexOf(id);
      if (destinationIndex >= 0) scrollToSection(destinationIndex, history);
    };

    const onHistoryNavigation = () => {
      const id = window.location.hash.slice(1) || "top";
      const destinationIndex = sectionIds.indexOf(id as (typeof sectionIds)[number]);
      if (destinationIndex >= 0) scrollToSection(destinationIndex, "none");
    };

    const onViewportChange = () => {
      updateViewportHeight();
      onScroll();
    };

    updateViewportHeight();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("popstate", onHistoryNavigation);
    window.addEventListener(SECTION_NAVIGATION_EVENT, onNavigationRequest);
    document.addEventListener("click", onAnchorClick);
    window.addEventListener("resize", onViewportChange);
    window.visualViewport?.addEventListener("resize", onViewportChange);
    desktopPager.addEventListener("change", onViewportChange);
    animationFrame = window.requestAnimationFrame(() => updateActiveSection());

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.cancelAnimationFrame(scrollFrame);
      window.clearTimeout(hashTimer);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("popstate", onHistoryNavigation);
      window.removeEventListener(SECTION_NAVIGATION_EVENT, onNavigationRequest);
      document.removeEventListener("click", onAnchorClick);
      window.removeEventListener("resize", onViewportChange);
      window.visualViewport?.removeEventListener("resize", onViewportChange);
      desktopPager.removeEventListener("change", onViewportChange);
      delete document.documentElement.dataset.chapterSettling;
    };
  }, []);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const context = gsap.context(() => {
      gsap.fromTo(
        ".hero__content > *",
        { y: 24, opacity: 0, filter: "blur(10px)" },
        { y: 0, opacity: 1, filter: "blur(0px)", duration: 1.15, stagger: 0.1, ease: "expo.out", delay: 0.15 },
      );
      gsap.fromTo(".nav-shell", { y: -18, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9, ease: "expo.out" });
    });
    return () => context.revert();
  }, []);

  return (
    <>
      <a className="skip-link" href="#evidence">Skip to product evidence</a>
      <Header condensed={navigationCondensed} setMode={setAccessMode} />
      <ChapterRail activeIndex={activeIndex} visible={navigationCondensed} />
      <NavigationDustMorph railVisible={navigationCondensed} />
      <ScrollMorph activeIndex={activeIndex} />
      <MobileAccessDock activeIndex={activeIndex} setMode={setAccessMode} />
      <MobileProgress progress={progress} />
      <main>
        <Hero cursorActive={activeIndex < 0} setMode={setAccessMode} />
        <EvidenceSection setMode={setAccessMode} />
        <TestimonialsSection />
        <AgentSection />
        <DataEvidenceSection />
        <ReportSection />
        <WaitlistExperience mode={accessMode} setMode={setAccessMode} />
        <FAQSection onRequestAccess={() => scrollToAccess("invite_beta", setAccessMode)} />
      </main>
      <Footer active={activeIndex === chapterPreviews.length - 1} setMode={setAccessMode} />
    </>
  );
}
